import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, Rss, Activity, ShieldCheck, ArrowUpRight, ChevronLeft, ChevronRight, Sparkles, HeartPulse } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api } from '../api/feedy';
import usePollWhileActive from '../hooks/usePollWhileActive';
import { CHANGELOG } from '../data/changelog';
import { StatusBadge, QualityBar, fmtDate, fmtNum, timeAgo } from '../components/feeds/shared';
import Flag from '../components/Flag';

const FEEDS_PER_PAGE = 7;

const TAG_STYLE = {
  performance: 'bg-emerald-500/10 text-emerald-600',
  feature: 'bg-sky-500/10 text-sky-600',
  reliability: 'bg-amber-500/10 text-amber-600',
  fix: 'bg-rose-500/10 text-rose-600',
};

const TONE_TEXT = { ok: 'text-emerald-600', warn: 'text-amber-600', bad: 'text-rose-600' };
const TONE_DOT = { ok: 'bg-emerald-500', warn: 'bg-amber-500', bad: 'bg-rose-500' };

function Stat({ icon: Icon, label, value, hint, tone, onClick, beat }) {
  return (
    <Card onClick={onClick} className={`p-4 ${onClick ? 'cursor-pointer transition-colors hover:bg-muted/40' : ''}`}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="font-mono text-[11px] uppercase tracking-wider">{label}</span>
      </div>
      <p className={`tnum mt-3 flex items-center gap-2 text-2xl font-semibold tracking-tight ${tone ? TONE_TEXT[tone] : ''}`}>
        {beat && tone === 'ok'
          ? <HeartPulse className="h-5 w-5 shrink-0 animate-heartbeat text-emerald-500" aria-label="healthy" />
          : tone && <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${TONE_DOT[tone]}`} />}
        {value}
      </p>
      {hint && <p className="mt-1 truncate text-xs text-muted-foreground">{hint}</p>}
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  // `soft` refetches without flipping the page spinner — used by the live poll.
  const load = (soft = false) => {
    if (!soft) setLoading(true);
    return Promise.all([api.sources.list(), api.feeds.list(), api.pipelineRuns.list().catch(() => [])])
      .then(([s, f, r]) => { setSources(s); setFeeds(f); setRuns(r); })
      .catch(() => {})
      .finally(() => { if (!soft) setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const instances = feeds.filter((f) => !f.is_template);
  // Latest refresh first (never-generated feeds sink to the end).
  const enabledFeeds = instances.filter((f) => f.enabled)
    .sort((a, b) => new Date(b.last_generated_at || 0) - new Date(a.last_generated_at || 0));
  const scored = instances.filter((f) => f.stats?.quality_score != null);
  const avgQuality = scored.length ? Math.round(scored.reduce((s, f) => s + f.stats.quality_score, 0) / scored.length) : null;

  // --- health: did everything run in today's nightly (small-hours) run? ---
  // The nightly runs in the early morning (≈02:00 UTC / 03–04:00 local), so
  // "fresh" = a sync/generation from TODAY (the local calendar day). Until ~7am,
  // before the run has finished, we judge against YESTERDAY so the pre-run window
  // isn't flagged stale. (The old "since 05:00 local" check broke once the run
  // started finishing BEFORE 05:00 — everything then read stale after 05:00.)
  const lastExpected = (() => {
    const now = new Date();
    const d = new Date(now); d.setHours(0, 0, 0, 0);       // start of today (local)
    if (now.getHours() < 7) d.setDate(d.getDate() - 1);    // pre-run grace → yesterday
    return d.getTime();
  })();
  const isFresh = (ts) => ts && new Date(ts).getTime() >= lastExpected;

  const lastRun = runs[0];
  const runFails = lastRun ? ((lastRun.summary?.sources_failed || 0) + (lastRun.summary?.feeds_failed || 0) + (lastRun.summary?.compares_failed || 0)) : 0;
  const runFresh = lastRun && isFresh(lastRun.finished_at || lastRun.started_at);
  const run = {
    value: !lastRun ? 'No runs yet' : lastRun.status === 'running' ? 'Running…'
      : (lastRun.status === 'error' || runFails) ? (runFails ? `${runFails} failed` : 'Failed')
      : !runFresh ? 'No run today' : 'Healthy',
    tone: !lastRun ? undefined : lastRun.status === 'running' ? 'warn'
      : (lastRun.status === 'error' || runFails) ? 'bad' : !runFresh ? 'warn' : 'ok',
    hint: lastRun ? `${lastRun.trigger} · ${fmtDate(lastRun.finished_at || lastRun.started_at)}` : 'never run',
  };

  const feedErrors = enabledFeeds.filter((f) => f.status === 'error').length;
  const feedsFresh = enabledFeeds.filter((f) => isFresh(f.last_generated_at)).length;
  const feedsStale = enabledFeeds.length - feedsFresh;
  const feedsHealth = {
    value: `${feedsFresh}/${enabledFeeds.length}`,
    tone: !enabledFeeds.length ? undefined : feedErrors ? 'bad' : feedsStale ? 'warn' : 'ok',
    hint: feedErrors ? `${feedErrors} errored` : feedsStale ? `${feedsStale} not refreshed today` : 'all current',
  };

  const srcErrors = sources.filter((s) => s.last_status === 'error').length;
  const srcFresh = sources.filter((s) => isFresh(s.last_synced_at)).length;
  const srcStale = sources.length - srcFresh;
  const srcHealth = {
    value: `${srcFresh}/${sources.length}`,
    tone: !sources.length ? undefined : srcErrors ? 'bad' : srcStale ? 'warn' : 'ok',
    hint: srcErrors ? `${srcErrors} errored` : srcStale ? `${srcStale} not synced today` : 'all current',
  };

  const qualTone = avgQuality == null ? undefined : avgQuality >= 95 ? 'ok' : avgQuality >= 85 ? 'warn' : 'bad';

  // Live-update while a source is syncing or a feed is generating.
  const anyRunning = useMemo(
    () => feeds.some((f) => f.status === 'running') || sources.some((s) => s.last_status === 'running'),
    [feeds, sources],
  );
  usePollWhileActive(anyRunning, () => load(true));

  // Only list active feeds on the dashboard — disabled ones don't run.
  const pageCount = Math.max(1, Math.ceil(enabledFeeds.length / FEEDS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pageFeeds = enabledFeeds.slice(safePage * FEEDS_PER_PAGE, (safePage + 1) * FEEDS_PER_PAGE);

  return (
    <AppLayout title="Dashboard" description="Your feed pipeline at a glance">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Activity} label="Last run" value={loading ? '—' : run.value} hint={loading ? '' : run.hint} tone={loading ? undefined : run.tone} beat />
        <Stat icon={Rss} label="Feeds up to date" value={loading ? '—' : feedsHealth.value} hint={loading ? '' : feedsHealth.hint} tone={loading ? undefined : feedsHealth.tone} onClick={() => navigate('/feeds')} />
        <Stat icon={Database} label="Sources synced" value={loading ? '—' : srcHealth.value} hint={loading ? '' : srcHealth.hint} tone={loading ? undefined : srcHealth.tone} onClick={() => navigate('/sources')} />
        <Stat icon={ShieldCheck} label="Avg quality" value={avgQuality == null ? '—' : `${avgQuality}%`} hint="across generated feeds" tone={loading ? undefined : qualTone} />
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card className="lg:order-2">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">Feeds</h2>
              {anyRunning && (
                <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-amber-600" title="A source or feed is running — updating automatically">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> live
                </span>
              )}
            </div>
            <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground" onClick={() => navigate('/feeds')}>
              View all <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="divide-y divide-border">
            {!loading && enabledFeeds.length === 0 && (
              <p className="px-5 py-10 text-center text-sm text-muted-foreground">No feeds yet.</p>
            )}
            {pageFeeds.map((f) => (
              <button key={f.id} onClick={() => navigate(`/feeds/${f.id}`)} className="flex w-full items-center gap-3 px-5 py-2.5 text-left hover:bg-muted/50">
                <Flag code={f.country} className="shrink-0 text-base" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{f.name}</p>
                  <p className="truncate font-mono text-[11px] text-muted-foreground">
                    {f.channel}{f.country ? ` · ${f.country.toLowerCase()}` : ''}
                    {f.last_generated_at && (
                      <span
                        className={isFresh(f.last_generated_at) ? 'text-emerald-600' : 'text-amber-600'}
                        title={`Last refreshed ${fmtDate(f.last_generated_at)}`}
                      >
                        {' · ↻ '}{timeAgo(f.last_generated_at)}
                      </span>
                    )}
                  </p>
                </div>
                <span className="tnum hidden w-20 text-right text-[13px] text-muted-foreground sm:block">{fmtNum(f.stats?.items_out)} out</span>
                <div className="w-32"><QualityBar score={f.stats?.quality_score} /></div>
                <div className="w-20 text-right"><StatusBadge status={f.status} /></div>
              </button>
            ))}
          </div>
          {pageCount > 1 && (
            <div className="flex items-center justify-between border-t border-border px-5 py-2.5">
              <span className="font-mono text-[11px] text-muted-foreground tnum">
                {safePage * FEEDS_PER_PAGE + 1}–{Math.min((safePage + 1) * FEEDS_PER_PAGE, enabledFeeds.length)} of {enabledFeeds.length}
              </span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} aria-label="Previous page">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="tnum px-1 font-mono text-[11px] text-muted-foreground">{safePage + 1} / {pageCount}</span>
                <Button variant="ghost" size="icon" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} aria-label="Next page">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Card className="lg:order-1">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h2 className="text-sm font-semibold">Sources</h2>
            <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground" onClick={() => navigate('/sources')}>
              Manage <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="divide-y divide-border">
            {!loading && sources.length === 0 && <p className="px-5 py-10 text-center text-sm text-muted-foreground">No sources.</p>}
            {sources.slice(0, 7).map((s) => (
              <div key={s.id} className="flex items-center justify-between px-5 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{s.name}</p>
                  <p className="truncate font-mono text-[11px] text-muted-foreground">{s.type}{s.locale ? ` · ${s.locale.toLowerCase()}` : ''} · {fmtNum(s.item_count)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2.5">
                  <StatusBadge status={s.last_status === 'success' && !isFresh(s.last_synced_at) ? 'idle' : s.last_status} />
                  <span className="text-[11px] text-muted-foreground">{fmtDate(s.last_synced_at) || 'never'}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="mt-6">
        <div className="flex items-center gap-2 border-b border-border px-5 py-3">
          <Sparkles className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold">What&apos;s new</h2>
          <span className="font-mono text-[11px] text-muted-foreground">latest changes &amp; features</span>
        </div>
        <div className="divide-y divide-border">
          {CHANGELOG.slice(0, 5).map((e, i) => (
            <div key={i} className="flex flex-col gap-1.5 px-5 py-3 sm:flex-row sm:gap-4">
              <div className="flex shrink-0 items-center gap-2 sm:w-44">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${TAG_STYLE[e.tag] || 'bg-muted text-muted-foreground'}`}>{e.tag}</span>
                <span className="font-mono text-[11px] text-muted-foreground tnum">{e.date}</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium">{e.title}</p>
                <ul className="mt-1 space-y-0.5">
                  {e.items.map((it, j) => (
                    <li key={j} className="flex gap-1.5 text-[12px] text-muted-foreground">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/50" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </AppLayout>
  );
}
